import React, { useContext, useEffect, useRef } from 'react';
import ReactSVG from 'react-svg';
import { Dialpad } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import * as lib from '~/dependencies';
import * as actions from '~/store/actions';
import { electron, log } from '~/helpers';
import { VideoContext } from '~/providers/VideoProvider';
import {
  IconNote,
  IconTag,
  IconPause,
  IconMute,
  IconHungUpCall,
  IconCamera,
} from '~/assets/svgIcons';
import { VideoState } from '~/store/reducers/callCurrent';
import {
  Wrapper,
  Container,
  CallItem,
  StatusText,
  FirstRow,
  SecondRow,
  BtnHangUp,
} from './styles';

export default function VideoActiveCall(props) {
  // REF'S
  const callTimeout = useRef(null);

  // HOOKS
  const { t } = lib.useTranslation();
  const dispatch = useDispatch();

  // CONTEXT'S
  const { acceptCall, rejectCall } = useContext(VideoContext);

  // CONST'S
  const { callId, from, to, fromName } = props;
  const defaultCallTimeout = 60000;

  const formatExt = name => {
    try {
      if (name) {
        return name.split('-')[2];
      }
    } catch (err) {
      log(`Failure to format the name`);
    }

    return name;
  };

  const accept = () => {
    const callObj = {
      ...props,
      state: VideoState.Establishing,
      isInviter: false,
    };
    electron.log(`NUACOM-LOG: [VIDEO] - Video call id: ${callId} is accepted.`);
    clearTimeout(callTimeout.current);
    dispatch(actions.updateVideoCall(callObj));
    acceptCall({ ...callObj, from: to, to: from });
    electron.startVideoCall(callObj);
    electron.hideNotification(callId);
  };

  const reject = isTimeout => {
    electron.log(`NUACOM-LOG: [VIDEO] - Video call id: ${callId} is rejected.`);
    clearTimeout(callTimeout.current);
    dispatch(actions.updateVideoCall(null));
    electron.hideNotification(callId);

    if (!isTimeout) {
      rejectCall(props);
    }
  };

  const startTimeout = () => {
    // The callee is responsible for the incoming call timeout due to the possibility of losing the signaling connection.
    callTimeout.current = setTimeout(() => {
      reject(true);
    }, defaultCallTimeout);
  };

  const handleIncomingCallNotification = (ev, msg) => {
    const { callAnswer } = msg;

    if (callAnswer === 'Answer') {
      accept();
    } else {
      reject(false);
    }
  };

  const listenForIncomingCalls = () => {
    if (window.isElectron) {
      window.ipcRenderer.on(
        'call-electron-notification',
        handleIncomingCallNotification
      );
    }
  };

  useEffect(() => {
    startTimeout();
    listenForIncomingCalls();

    return () => {
      if (window.isElectron) {
        window.ipcRenderer.removeListener(
          'call-electron-notification',
          handleIncomingCallNotification
        );
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper>
      <Container>
        <CallItem>
          <FirstRow>
            <div className="left">
              <div>
                <Typography variant="subtitle1" align="left">
                  <strong>{fromName}</strong>
                </Typography>
              </div>
              <div className="informations">
                <Typography variant="subtitle2" align="left">
                  <strong>{formatExt(from)}</strong>
                </Typography>
              </div>
            </div>
            <div className="right">
              <StatusText>Incoming Video Call</StatusText>
            </div>
          </FirstRow>
          <SecondRow answered={false} dtmf={false}>
            <div className="actions-buttons">
              <button
                type="button"
                onClick={() => {}}
                disabled
                title={t('components.activeCalls.btn.mute')}>
                <ReactSVG src={IconMute} className="icon-btn" />
              </button>

              <button
                type="button"
                onClick={() => {}}
                disabled
                title={t('components.activeCalls.btn.hold')}>
                <ReactSVG src={IconPause} className="icon-btn" />
              </button>

              <button
                type="button"
                onClick={() => {}}
                disabled
                title={t('components.activeCalls.btn.tags')}>
                <ReactSVG src={IconTag} className="icon-btn" />
              </button>

              <button
                type="button"
                onClick={() => {}}
                disabled
                title={t('components.activeCalls.btn.notes')}>
                <ReactSVG src={IconNote} className="icon-btn" />
              </button>

              <div className="dtmfBox">
                <button
                  type="button"
                  onClick={() => {}}
                  title={t('components.activeCalls.btn.inCallKeyboard')}
                  className="dtmfBtn"
                  disabled={false}>
                  <Dialpad className="dial-btn" />
                </button>
              </div>
            </div>

            <button
              type="button"
              onClick={accept}
              className="action-btn action-one">
              <div className="green">
                <ReactSVG src={IconCamera} className="icon-call" />
              </div>
            </button>

            <BtnHangUp
              onClick={() => reject(false)}
              className="action-btn action-two"
              finished={false}
              disabled={false}
              type="button">
              <div className="red">
                <ReactSVG src={IconHungUpCall} className="icon-call" />
              </div>
            </BtnHangUp>
          </SecondRow>
        </CallItem>
      </Container>
    </Wrapper>
  );
}

VideoActiveCall.defaultProps = {
  from: '',
  to: '',
  fromName: '',
  callId: '',
};

VideoActiveCall.propTypes = {
  from: lib.PropTypes.string,
  to: lib.PropTypes.string,
  fromName: lib.PropTypes.string,
  callId: lib.PropTypes.string,
};
